/**
 * 학원 신청 — 개별 항목 편집 (풀스크린)
 * /academy/apply/edit/:field
 *
 * 진입 시 본인의 draft 학원을 불러와 해당 필드만 편집.
 * 저장 시 Firestore에 즉시 반영 후 신청 리스트로 복귀.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack, IoAdd, IoClose, IoLogoYoutube, IoPlay } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import academyService from '../../services/academyService';
import { parseYouTubeId, ytThumb } from '../Console/AcademyForm';
import BrandLoader from '../../components/common/BrandLoader';
import DaumPostcodeModal from '../../components/common/DaumPostcodeModal';

const FIELD_META = {
  field: {
    label: '분야',
    title: '어떤 분야의 학원인가요?',
    sub: '학원 종류를 먼저 선택해 주세요.',
    kind: 'select',
    options: [
      { v: 'study', l: '교과 과정', d: '영어 · 수학 · 국어 · 논술 등' },
      { v: 'sports', l: '운동 / 태권도', d: '태권도 · 복싱 · 줄넘기 등' },
      { v: 'arts', l: '음악 / 미술', d: '피아노 · 미술 · 무용 등' },
    ],
  },
  type: {
    label: '운영 형태',
    title: '운영 형태를 알려주세요',
    sub: '규모에 맞는 형태를 골라 주세요.',
    kind: 'select',
    options: [
      { v: 'academy', l: '학원', d: '여러 강사 · 여러 반 운영' },
      { v: 'home', l: '교습소 · 공부방', d: '1인 운영' },
      { v: 'personal', l: '개인레슨', d: '과외 · 1:1 레슨' },
    ],
  },
  name: {
    label: '학원 이름',
    title: '학원 이름이 무엇인가요?',
    sub: '간판에 적힌 이름 그대로 입력해 주세요.',
    kind: 'text',
  },
  subjects: {
    label: '과목',
    title: '어떤 과목을 가르치나요?',
    sub: '여러 개면 하나씩 추가해 주세요. 예: 영어',
    kind: 'chips',
  },
  grades: {
    label: '수업 대상',
    title: '수업 대상이 어떻게 되나요?',
    sub: '대상을 하나씩 추가해 주세요. 예: 초등학생',
    kind: 'lines',
  },
  location: {
    label: '주소',
    title: '학원이 어디에 있나요?',
    sub: '주소 검색으로 입력해 주세요.',
    kind: 'address',
  },
  phone: {
    label: '연락처',
    title: '연락처를 알려주세요',
    sub: '학부모 상담 전화번호로 쓰여요.',
    kind: 'text',
    inputMode: 'tel',
  },
  hours: {
    label: '영업시간',
    title: '영업시간을 알려주세요',
    sub: '요일대별로 한 줄씩 추가해 주세요. 예: 평일 14:00~22:00',
    kind: 'lines',
  },
  shuttle: {
    label: '셔틀버스',
    title: '셔틀버스를 운행하나요?',
    sub: '학부모가 학원을 고를 때 참고해요.',
    kind: 'toggle',
  },
  intro: {
    label: '학원 소개',
    title: '학원을 소개해 주세요',
    sub: '어떤 학원인지 알려주세요.',
    kind: 'textarea',
  },
  tuition: {
    label: '학원비',
    title: '학원비를 알려주세요',
    sub: '항목별로 추가해 주세요. 나중에 입력해도 괜찮아요.',
    kind: 'pairs',
  },
  hashtags: {
    label: '해시태그',
    title: '해시태그를 추가해 주세요',
    sub: '검색에 도움이 돼요. 예: 목동영어학원',
    kind: 'chips',
  },
  links: {
    label: '홈페이지 · SNS',
    title: '홈페이지·SNS가 있나요?',
    sub: '없으면 건너뛰어도 괜찮아요.',
    kind: 'links',
  },
  videos: {
    label: '수업영상',
    title: '수업영상을 보여주세요',
    sub: '유튜브 등 영상 링크를 하나씩 추가해 주세요. 없으면 건너뛰어도 괜찮아요.',
    kind: 'videos',
  },
  promoPhotos: {
    label: '사진',
    title: '학원 사진을 등록해 주세요',
    sub: '여러 장 올려 학원을 홍보하세요. 맨 위 사진이 대표로 쓰여요. 사진 설명도 적을 수 있어요.',
    kind: 'photos',
  },
};

const AcademyApplyEditPage = () => {
  const { field } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const meta = FIELD_META[field];

  const [academy, setAcademy] = useState(null);
  const [val, setVal] = useState(null);
  const [linkVal, setLinkVal] = useState({ homepage: '', instagram: '', blog: '' });
  const [draft, setDraft] = useState('');
  const [vTitle, setVTitle] = useState('');
  const [vUrl, setVUrl] = useState('');
  const [pairLabel, setPairLabel] = useState('');
  const [pairPrice, setPairPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [postcodeOpen, setPostcodeOpen] = useState(false);

  useEffect(() => {
    if (!userData?.uid || !meta) return;
    (async () => {
      const a = await academyService.getMyDraftAcademy(userData.uid);
      if (!a) {
        navigate('/academy/apply', { replace: true });
        return;
      }
      setAcademy(a);
      if (
        meta.kind === 'chips' || meta.kind === 'lines'
        || meta.kind === 'pairs' || meta.kind === 'videos'
      ) {
        setVal(a[field] || []);
      } else if (meta.kind === 'photos') {
        // 대표사진(photoUrl) + 홍보사진(promoPhotos)을 한 배열로 합쳐서 다룸 (첫 장이 대표)
        const promo = (a.promoPhotos || []).map((p) => (typeof p === 'string' ? { url: p, label: '' } : p));
        setVal(a.photoUrl ? [{ url: a.photoUrl, label: '' }, ...promo] : promo);
      } else if (meta.kind === 'toggle') {
        setVal(typeof a[field] === 'boolean' ? a[field] : null);
      } else if (meta.kind === 'links') {
        setLinkVal({
          homepage: a.homepage || '',
          instagram: a.instagram || '',
          blog: a.blog || '',
        });
      } else {
        setVal(a[field] || '');
      }
      setLoading(false);
    })();
  }, [field, meta, navigate, userData?.uid]);

  if (!meta) {
    return (
      <Wrap>
        <Top>
          <BackBtn type="button" onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
          <TopTitle>알 수 없는 항목</TopTitle>
        </Top>
      </Wrap>
    );
  }
  if (loading || !academy) return <BrandLoader />;

  const addItem = () => {
    const v = draft.trim();
    if (!v) return;
    if (!val.includes(v)) setVal([...val, v]);
    setDraft('');
  };
  const addPair = () => {
    const l = pairLabel.trim();
    const p = pairPrice.trim();
    if (!l || !p) return;
    setVal([...val, { label: l, price: p }]);
    setPairLabel('');
    setPairPrice('');
  };
  const removeAt = (i) => setVal(val.filter((_, idx) => idx !== i));
  // 해당 사진을 맨 앞(대표)으로 이동
  const moveToFront = (i) => {
    if (i <= 0) return;
    const arr = [...val];
    const [m] = arr.splice(i, 1);
    arr.unshift(m);
    setVal(arr);
  };
  const addVideo = () => {
    const title = vTitle.trim();
    const url = vUrl.trim();
    const videoId = parseYouTubeId(url);
    if (!title) { alert('영상 제목을 입력해 주세요.'); return; }
    if (!videoId) { alert('유튜브 주소가 올바르지 않아요.\n예) https://youtu.be/xxxxxxxxxxx'); return; }
    if (val.some((x) => (x.videoId || parseYouTubeId(x.url)) === videoId)) { setVTitle(''); setVUrl(''); return; }
    setVal([...val, { title, url, videoId }]);
    setVTitle(''); setVUrl('');
  };

  const openPostcode = () => setPostcodeOpen(true);

  const onAddPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !academy) return;
    setUploadingPhoto(true);
    try {
      const url = await academyService.uploadAcademyPromoPhoto(academy.id, file);
      setVal([...(val || []), { url, label: '' }]);
    } catch (err) {
      console.error('홍보사진 업로드 실패:', err);
      alert('사진 업로드에 실패했어요.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const valid = (() => {
    switch (meta.kind) {
      case 'text':
      case 'address':
      case 'textarea': return !!String(val || '').trim();
      case 'select': return !!val;
      case 'toggle': return val !== null;
      case 'chips':
      case 'lines': return Array.isArray(val) && val.length > 0;
      case 'pairs': return true; // 선택 항목
      case 'links': return true; // 선택 항목
      case 'photos': return Array.isArray(val) && val.length > 0; // 최소 1장(=대표) 필수
      case 'videos': return true; // 선택 항목
      default: return false;
    }
  })();

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      let patch;
      if (meta.kind === 'links') {
        patch = {
          homepage: linkVal.homepage.trim(),
          instagram: linkVal.instagram.trim(),
          blog: linkVal.blog.trim(),
        };
      } else if (meta.kind === 'text' || meta.kind === 'address' || meta.kind === 'textarea') {
        patch = { [field]: typeof val === 'string' ? val.trim() : val };
      } else if (meta.kind === 'photos') {
        // 첫 장 = 대표(photoUrl), 나머지 = 홍보사진(promoPhotos)으로 분리 저장
        patch = { photoUrl: val[0]?.url || '', promoPhotos: val.slice(1) };
      } else {
        patch = { [field]: val };
      }
      // subjects의 첫 값은 subject로도 저장 (디렉토리 검색용)
      if (field === 'subjects') patch.subject = (val[0] || '');
      await academyService.updateDraftAcademy(academy.id, patch);
      navigate('/academy/apply', { replace: true });
    } catch (err) {
      console.error('항목 저장 실패:', err);
      alert('저장에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <Top>
        <BackBtn type="button" onClick={() => navigate(-1)}><IoArrowBack size={22} /></BackBtn>
        <TopTitle>{meta.label}</TopTitle>
      </Top>

      <Content>
        <QTitle>{meta.title}</QTitle>
        <QSub>{meta.sub}</QSub>

        <Field>
          {meta.kind === 'select' && meta.options.map((o) => (
            <Choice
              key={o.v}
              type="button"
              $on={val === o.v}
              onClick={() => setVal(o.v)}
            >
              <ChoiceLabel>{o.l}</ChoiceLabel>
              <ChoiceDesc>{o.d}</ChoiceDesc>
            </Choice>
          ))}

          {meta.kind === 'toggle' && (
            <ChoiceRow>
              <Choice type="button" $on={val === true} onClick={() => setVal(true)}>
                <ChoiceLabel>운행해요</ChoiceLabel>
              </Choice>
              <Choice type="button" $on={val === false} onClick={() => setVal(false)}>
                <ChoiceLabel>운행 안 해요</ChoiceLabel>
              </Choice>
            </ChoiceRow>
          )}

          {meta.kind === 'text' && (
            <TextInput
              autoFocus
              value={val}
              inputMode={meta.inputMode || 'text'}
              onChange={(e) => setVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSave(); }}
            />
          )}

          {meta.kind === 'address' && (
            <AddrBtn type="button" $on={!!val} onClick={openPostcode}>
              {val || '주소 검색'}
            </AddrBtn>
          )}

          {meta.kind === 'textarea' && (
            <TextArea
              autoFocus
              rows={6}
              value={val}
              onChange={(e) => setVal(e.target.value)}
            />
          )}

          {(meta.kind === 'chips' || meta.kind === 'lines') && (
            <>
              <AddRow>
                <TextInput
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                />
                <IconBtn type="button" onClick={addItem}><IoAdd size={20} /></IconBtn>
              </AddRow>
              {meta.kind === 'chips' ? (
                <Chips>
                  {val.map((v, i) => (
                    <Chip key={i} type="button" onClick={() => removeAt(i)}>
                      {v} <IoClose size={13} />
                    </Chip>
                  ))}
                </Chips>
              ) : (
                <Lines>
                  {val.map((v, i) => (
                    <LineRow key={i}>
                      <span>{v}</span>
                      <LineX type="button" onClick={() => removeAt(i)}><IoClose size={16} /></LineX>
                    </LineRow>
                  ))}
                </Lines>
              )}
            </>
          )}

          {meta.kind === 'pairs' && (
            <>
              <PairInputs>
                <TextInput
                  value={pairLabel}
                  onChange={(e) => setPairLabel(e.target.value)}
                />
                <TextInput
                  value={pairPrice}
                  onChange={(e) => setPairPrice(e.target.value)}
                />
                <IconBtn type="button" onClick={addPair}><IoAdd size={20} /></IconBtn>
              </PairInputs>
              <PairHint>왼쪽: 항목 (예: 주 2회 90분) · 오른쪽: 금액 (예: 월 22만원)</PairHint>
              <Lines>
                {val.map((t, i) => (
                  <LineRow key={i}>
                    <span>{t.label} · {t.price}</span>
                    <LineX type="button" onClick={() => removeAt(i)}><IoClose size={16} /></LineX>
                  </LineRow>
                ))}
              </Lines>
            </>
          )}

          {meta.kind === 'photos' && (
            <PhotoBlock>
              {(val || []).map((p, i) => (
                <PhotoItem key={i}>
                  <PhotoThumbBig $url={p.url}>
                    {i === 0
                      ? <RepBadgeBig>대표</RepBadgeBig>
                      : <SetRepBtn type="button" onClick={() => moveToFront(i)}>대표로 지정</SetRepBtn>}
                  </PhotoThumbBig>
                  <PhotoLabelInput
                    value={p.label || ''}
                    onChange={(e) => {
                      const next = [...val];
                      next[i] = { ...next[i], label: e.target.value };
                      setVal(next);
                    }}
                  />
                  <PhotoRemoveBtn
                    type="button"
                    onClick={() => setVal(val.filter((_, idx) => idx !== i))}
                  >
                    <IoClose size={18} />
                  </PhotoRemoveBtn>
                </PhotoItem>
              ))}
              <AddPhotoLabel as="label">
                {uploadingPhoto ? '업로드 중...' : '+ 사진 추가'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onAddPhoto}
                  disabled={uploadingPhoto}
                />
              </AddPhotoLabel>
            </PhotoBlock>
          )}

          {meta.kind === 'videos' && (
            <>
              <VideoBox>
                <MiniLab>① 영상 제목</MiniLab>
                <TextInput
                  autoFocus
                  value={vTitle}
                  placeholder="예: 유리드믹스 수업 미리보기"
                  onChange={(e) => setVTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
                />
                <MiniLab>② 유튜브 주소</MiniLab>
                <YtRow>
                  <YtInputWrap>
                    <IoLogoYoutube size={20} color="#FF0000" />
                    <YtInput
                      value={vUrl}
                      inputMode="url"
                      placeholder="https://youtu.be/… 붙여넣기"
                      onChange={(e) => setVUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); } }}
                    />
                  </YtInputWrap>
                  <IconBtn type="button" onClick={addVideo}><IoAdd size={20} /></IconBtn>
                </YtRow>
              </VideoBox>
              <Lines>
                {val.map((v, i) => {
                  const obj = typeof v === 'string' ? { title: v } : v;
                  const vid = obj.videoId || parseYouTubeId(obj.url);
                  return (
                    <VideoRow key={i}>
                      {vid
                        ? <VThumb $url={ytThumb(vid)}><IoPlay size={16} /></VThumb>
                        : <VThumbEmpty><IoLogoYoutube size={20} color="#9CA3AF" /></VThumbEmpty>}
                      <VMeta>
                        <VTitleTxt>{obj.title || '제목 없음'}</VTitleTxt>
                        <VideoUrl>{obj.url || ''}</VideoUrl>
                      </VMeta>
                      <LineX type="button" onClick={() => removeAt(i)}><IoClose size={16} /></LineX>
                    </VideoRow>
                  );
                })}
              </Lines>
            </>
          )}

          {meta.kind === 'links' && (
            <LinkFields>
              <FieldLabel>홈페이지</FieldLabel>
              <TextInput
                value={linkVal.homepage}
                inputMode="url"
                onChange={(e) => setLinkVal({ ...linkVal, homepage: e.target.value })}
              />
              <FieldLabel>인스타그램</FieldLabel>
              <TextInput
                value={linkVal.instagram}
                inputMode="url"
                onChange={(e) => setLinkVal({ ...linkVal, instagram: e.target.value })}
              />
              <FieldLabel>블로그</FieldLabel>
              <TextInput
                value={linkVal.blog}
                inputMode="url"
                onChange={(e) => setLinkVal({ ...linkVal, blog: e.target.value })}
              />
            </LinkFields>
          )}
        </Field>
      </Content>

      <Bottom>
        <SaveBtn
          type="button"
          disabled={!valid || saving}
          onClick={onSave}
        >
          {saving ? '저장 중...' : '저장'}
        </SaveBtn>
      </Bottom>

      {postcodeOpen && (
        <DaumPostcodeModal
          onClose={() => setPostcodeOpen(false)}
          onComplete={(addr) => setVal(addr)}
        />
      )}
    </Wrap>
  );
};

export default AcademyApplyEditPage;

/* ── styled ─────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 6px;
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopTitle = styled.p`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Content = styled.div`
  flex: 1;
  padding: 22px 20px 24px;
`;

const QTitle = styled.p`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const QSub = styled.p`
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 8px;
`;

const Field = styled.div`
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FieldLabel = styled.label`
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 4px;
`;

const Choice = styled.button`
  width: 100%;
  text-align: left;
  padding: 16px;
  border-radius: 12px;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F4F5F7')};
`;

const ChoiceRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ChoiceLabel = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ChoiceDesc = styled.span`
  display: block;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 14px 15px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 16px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const AddrBtn = styled.button`
  width: 100%;
  padding: 15px 16px;
  border: 0;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  text-align: left;
  color: ${({ $on, theme }) => ($on ? theme.colors.text.primary : theme.colors.text.hint)};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 15px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  resize: vertical;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
`;

const IconBtn = styled.button`
  flex-shrink: 0;
  width: 50px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const Chip = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 700;
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const LineRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LineX = styled.button`
  background: none;
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const VideoUrl = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VideoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 14px;
  border-radius: 12px;
  background: #FBFCFE;
  border: 1px solid #EAEDF3;
`;

const MiniLab = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const YtRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

const YtInputWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 14px;
  border-radius: 11px;
  background: #F4F5F7;
  &:focus-within { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
  svg { flex-shrink: 0; }
`;

const YtInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 0;
  border: 0;
  background: transparent;
  font-size: 16px;
  &:focus { outline: none; }
`;

const VideoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border-radius: 11px;
  background: #F4F5F7;
`;

const VThumb = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 45px;
  border-radius: 7px;
  background: ${({ $url }) => `#000 url(${$url}) center/cover`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg { filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)); }
`;

const VThumbEmpty = styled.div`
  flex-shrink: 0;
  width: 72px;
  height: 45px;
  border-radius: 7px;
  background: #E9ECF1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const VTitleTxt = styled.span`
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PairInputs = styled.div`
  display: flex;
  gap: 8px;
`;

const PairHint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const LinkFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PhotoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PhotoItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 14px;
  background: #F8F9FB;
  border: 1px solid #ECEDF1;
`;

const PhotoThumbBig = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  background: ${({ $url }) => ($url ? `#EDEFF3 url(${$url}) center/cover` : '#EDEFF3')};
`;

const RepBadgeBig = styled.span`
  position: absolute;
  left: 8px;
  top: 8px;
  padding: 4px 11px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
`;

const SetRepBtn = styled.button`
  position: absolute;
  left: 8px;
  top: 8px;
  padding: 5px 11px;
  border-radius: 9px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  backdrop-filter: blur(4px);
`;

const PhotoLabelInput = styled.input`
  width: 100%;
  padding: 11px 13px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const PhotoRemoveBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

const AddPhotoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  border-radius: 14px;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const Bottom = styled.div`
  position: sticky;
  bottom: 0;
  padding: 12px 16px 22px;
  background: #fff;
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;
